import { Box, TableCell, TableRow, Typography } from '@mui/material';
import { format } from 'date-fns';
import { he } from 'date-fns/locale';
import React from 'react';
import { useSnackbar } from 'notistack';
import awsConfig from '../../../../amplify_outputs.json';

interface Report {
  id: string;
  fileName: string;
  fullPath: string;
  fileExtension: string;
  type: 'INPUT' | 'OUTPUT';
  status: string;
  date: string;
  senderName: string;
  fileType: string; // EMPFED/FEDBKA for INPUT files
  processed: boolean;
  createdAt: string;
  updatedAt: string;
  executionDate?: string;
  // Fields for compatibility
  lastModified: string;
  fileSize: number;
  eTag: string;
  storageClass: string;
}

interface Folder {
  folderName: string;
  fullPath: string;
  type: string;
  fileCount?: number;
}

interface RowProps {
  item: Report | Folder;
  type: 'file' | 'folder';
}

const ReportItem: React.FC<RowProps> = ({ item, type }) => {
  const { enqueueSnackbar } = useSnackbar();

  // Function to handle file download
  const handleDownload = async (e: any) => {
    e.stopPropagation();
    if (!isFolder && item.fullPath) {
      const bucketName = awsConfig.storage.bucket_name;
      const fileKey = item.fullPath;
      const s3Url = `https://${bucketName}.s3.amazonaws.com/${fileKey}`;

      try {
        const response = await fetch(s3Url);
        if (!response.ok) {
          throw new Error(`Error downloading file: ${response.statusText}`);
        }

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = (item as Report).fileName || 'file';
        a.click();
        window.URL.revokeObjectURL(url);

        enqueueSnackbar('הקובץ ירד בהצלחה!', { variant: 'success' });
      } catch {
        enqueueSnackbar('שגיאה בהורדת הקובץ', { variant: 'error' });
      }
    } else {
      enqueueSnackbar('לא ניתן להוריד את הקובץ', { variant: 'error' });
    }
  };

  const isFolder = type === 'folder';
  const fileItem = item as Report;
  const folderItem = item as Folder;

  return (
    <TableRow
      sx={{
        backgroundColor: '#F2F4F7',
        borderRadius: '10px',
        p: '20px',
        overflow: 'hidden',
        '&:last-child td, &:last-child th': { border: 0 },
        boxShadow: 'none',
        textAlign: 'right',
        cursor: isFolder ? 'pointer' : 'default',
      }}
      onClick={isFolder ? () => console.log('Navigate to folder:', folderItem.fullPath) : undefined}
    >
      {/* File/folder name */}
      <TableCell
        sx={{
          borderTopRightRadius: '10px',
          borderBottomRightRadius: '10px',
          padding: '20px',
          borderBottom: 'none',
        }}
      >
        <Box
          sx={{
            textAlign: 'right',
            display: 'flex',
            gap: '10px',
            alignItems: 'center',
          }}
        >
          <Typography
            dir="rtl"
            fontSize="14px"
            fontWeight="700"
            fontFamily={'AlmoniDL'}
          >
            {isFolder ? folderItem.folderName : fileItem.fileName}
          </Typography>
        </Box>
      </TableCell>

      {/* File type (INPUT/OUTPUT) */}
      <TableCell
        sx={{
          padding: '20px',
          borderBottom: 'none',
        }}
      >
        <Box
          sx={{
            textAlign: 'right',
            display: 'flex',
            flexDirection: 'column',
            gap: '2px',
          }}
        >
          <Typography
            dir="rtl"
            fontSize="14px"
            fontWeight="700"
            fontFamily={'AlmoniDL'}
          >
            {isFolder ? '-' : fileItem.type}
          </Typography>
        </Box>
      </TableCell>

      {/* Date */}
      <TableCell
        sx={{
          padding: '20px',
          borderBottom: 'none',
        }}
      >
        <Box
          sx={{
            textAlign: 'right',
            display: 'flex',
            flexDirection: 'column',
            gap: '2px',
          }}
        >
          <Typography
            dir="rtl"
            fontSize="14px"
            fontWeight="700"
            fontFamily={'AlmoniDL'}
          >
            {isFolder 
              ? '-' 
              : format(new Date(fileItem.date || fileItem.createdAt), 'dd/MM/yyyy', {
                  locale: he,
                })
            }
          </Typography>
        </Box>
      </TableCell>

      {/* Sender name (employer) */}
      <TableCell
        sx={{
          padding: '20px',
          borderBottom: 'none',
        }}
      >
        <Box
          sx={{
            textAlign: 'right',
            display: 'flex',
            flexDirection: 'column',
            gap: '2px',
          }}
        >
          <Typography
            dir="rtl"
            fontSize="14px"
            fontWeight="700"
            fontFamily={'AlmoniDL'}
          >
            {isFolder ? '-' : (fileItem.senderName || 'לא ידוע')}
          </Typography>
        </Box>
      </TableCell>

      {/* Download button */}
      <TableCell
        sx={{
          padding: '20px',
          borderBottom: 'none',
          borderTopLeftRadius: '10px',
          borderBottomLeftRadius: '10px',
        }}
      >
        <Box
          sx={{
            textAlign: 'right',
            display: 'flex',
            flexDirection: 'column',
            gap: '2px',
          }}
        >
          {isFolder ? (
            <Typography
              dir="rtl"
              fontSize="14px"
              fontWeight="700"
              fontFamily={'AlmoniDL'}
            >
              -
            </Typography>
          ) : (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '45px',
                height: '45px',
                background: 'white',
                borderRadius: '10px',
                cursor: 'pointer',
              }}
              onClick={(e) => handleDownload(e)}
            >
              <img alt="download" src={'/assets/dashboard/download.svg'} />
            </Box>
          )}
        </Box>
      </TableCell>
    </TableRow>
  );
};

export default ReportItem;
